/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import {
  FolderOutlined,
  FileTextOutlined,
  GlobalOutlined,
  FolderOpenOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag, Typography, Button, Tooltip, Popconfirm } from "antd"

const { Text } = Typography
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"

interface SystemSetTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  onRowClick?: (record: any) => void
  onEdit?: (record: any) => void
  onDelete?: (record: any) => void
  onViewOnline?: (record: any) => void
  pagination?: TableProps["pagination"]
}

const SystemSetTable: React.FC<SystemSetTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onRowClick,
  onEdit,
  onDelete,
  onViewOnline,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 85,
      align: "center",
      render: (value: number) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Tên",
      key: "set_info",
      width: 300,
      fixed: "left",
      render: (_: any, record: any) => (
        <div
          className="flex cursor-pointer flex-col gap-0.5 hover:opacity-80"
          onClick={(e) => {
            e.stopPropagation()
            if (onRowClick) onRowClick(record)
          }}
        >
          <div className="flex items-center gap-2">
            {record.image_url || record.image ? (
              <img
                src={record.image_url || record.image}
                alt={record.title}
                className="h-7 w-7 shrink-0 rounded-md border border-gray-200 object-cover"
              />
            ) : record.node_type === "folder" ? (
              <FolderOutlined style={{ color: record.color || "#1677ff", fontSize: "18px" }} />
            ) : (
              <FileTextOutlined style={{ color: record.color || "#52c41a", fontSize: "18px" }} />
            )}
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-primary">{record.title}</span>
                {Array.isArray(record.tags) && record.tags.map((t: string) => (
                  <Tag key={t} color="cyan" className="text-[10px] m-0 py-0 px-1 font-semibold">
                    {t}
                  </Tag>
                ))}
              </div>
              {record.full_title && record.full_title !== record.title && (
                <Text type="secondary" className="text-xs italic">
                  {record.full_title}
                </Text>
              )}
            </div>
          </div>
          {record.description && (
            <div className="line-clamp-2">
              <Text type="secondary" className="text-xs">
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },

    {
      title: "Loại",
      key: "node_type",
      width: 100,
      align: "center",
      render: (_: any, record: any) => {
        if (record.node_type === "folder") {
          return <Tag color="blue">Thư mục</Tag>
        }
        return <Tag color="green">Bộ từ</Tag>
      },
    },
    {
      title: "Số lượng",
      key: "counts",
      width: 100,
      align: "center",
      render: (_: any, record: any) => {
        if (record.node_type === "folder") {
          return <Text strong>{record.children_count ?? 0} mục</Text>
        }
        return <Text strong>{record.vocabulary_count ?? 0} từ</Text>
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      align: "center",
      render: (value: string) => (value ? formatDateTime(value, "dd/mm/yy hh:mm") : "_"),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 160,
      align: "center",
      render: (_: any, record: any) => {
        return (
          <div className="flex items-center justify-center gap-2">
            <Tooltip title="Xem online">
              <Button
                type="text"
                size="small"
                icon={<GlobalOutlined className="text-blue-500" />}
                onClick={(e) => {
                  e.stopPropagation()
                  onViewOnline && onViewOnline(record)
                }}
              />
            </Tooltip>
            <Tooltip title={record.node_type === "folder" ? "Mở vào trong" : "Thêm/sửa từ vựng"}>
              <Button
                type="text"
                size="small"
                icon={<FolderOpenOutlined className="text-green-600" />}
                onClick={(e) => {
                  e.stopPropagation()
                  if (onRowClick) onRowClick(record)
                }}
              />
            </Tooltip>
            <Tooltip title="Sửa thông tin">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined className="text-orange-500" />}
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit && onEdit(record)
                }}
              />
            </Tooltip>
            {(() => {
              const canDelete = record.node_type === "folder" ? record.children_count == 0 : true // Cho phép xoá bộ từ dù có từ vựng hay không
              return (
                <Tooltip title={!canDelete ? "Không thể xoá vì có chứa dữ liệu con" : "Xoá"}>
                  <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-block" }}>
                    <Popconfirm
                      title="Xác nhận xoá"
                      description="Bạn có chắc chắn muốn xoá mục này không?"
                      onConfirm={(e) => {
                        e?.stopPropagation()
                        onDelete && onDelete(record)
                      }}
                      onCancel={(e) => e?.stopPropagation()}
                      disabled={!canDelete}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!canDelete}
                        style={!canDelete ? { pointerEvents: "none" } : {}}
                      />
                    </Popconfirm>
                  </span>
                </Tooltip>
              )
            })()}
          </div>
        )
      },
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

SystemSetTable.displayName = "SystemSetTable"

export default SystemSetTable
